import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { ArrowLeft, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/trpc';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { waitForClerk } from '@/utils/clerk';
import { analytics } from '@/lib/analytics';
import {
  InterviewStartScreen,
  InterviewActiveScreen,
  InterviewResultsScreen,
  hasSpeechRecognitionSupport,
} from '@/components/mock-interview';

type UiState = 'start' | 'loading' | 'active' | 'results';

interface PendingFeedback {
  questionId: string;
  answerScore: number;
  feedback: string;
  expectedAnswer: string | null;
}

interface InterviewQuestion {
  id: string;
  orderIndex: number;
  questionText: string;
  questionType: string;
  userAnswer: string | null;
  answerScore: number | null;
  feedback: string | null;
}

interface MutationError {
  message?: string;
}

export const Route = createFileRoute('/applications/$lookupId/interview')({
  validateSearch: (search: Record<string, unknown>) => ({
    interviewId:
      typeof search.interviewId === 'string' && search.interviewId.length > 0
        ? search.interviewId
        : undefined,
    // Preserve the origin tab so returning to the application keeps the
    // correct "back" destination.
    from: typeof search.from === 'string' ? search.from : undefined,
  }),
  beforeLoad: async () => {
    const user = await waitForClerk();
    if (!user) {
      return redirect({ to: '/', replace: true });
    }
  },
  component: ApplicationInterviewPage,
});

function ApplicationInterviewPage() {
  const { lookupId } = Route.useParams();
  const { interviewId: searchInterviewId, from } = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useUser();

  const [uiState, setUiState] = useState<UiState>(searchInterviewId ? 'results' : 'start');
  const [interviewId, setInterviewId] = useState<string | null>(searchInterviewId ?? null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [pendingFeedback, setPendingFeedback] = useState<PendingFeedback | null>(null);

  const utils = trpc.useUtils();

  const applicationQuery = trpc.applications.getByLookupId.useQuery(
    { lookupId },
    { enabled: !!user?.id }
  );

  useEffect(() => {
    if (
      applicationQuery.data &&
      user?.id &&
      applicationQuery.data.userId !== user.id
    ) {
      navigate({ to: '/', replace: true });
    }
  }, [applicationQuery.data, user?.id, navigate]);

  const usageQuery = trpc.mockInterview.getUsageStats.useQuery(undefined, {
    enabled: !!user?.id,
  });

  const interviewQuery = trpc.mockInterview.getInterview.useQuery(
    { interviewId: interviewId ?? '' },
    { enabled: !!interviewId && uiState !== 'loading' }
  );

  useEffect(() => {
    if (!interviewQuery.data) return;
    if (interviewQuery.data.status === 'completed' || interviewQuery.data.status === 'abandoned') {
      setUiState('results');
    }
  }, [interviewQuery.data]);

  const startMutation = trpc.mockInterview.startInterview.useMutation({
    onSuccess: (data: { interviewId: string }) => {
      setInterviewId(data.interviewId);
      setCurrentIndex(0);
      setPendingFeedback(null);
      setUiState('active');
      utils.mockInterview.getInterview.invalidate({ interviewId: data.interviewId });
      utils.mockInterview.getUsageStats.invalidate();
      analytics.track('mock_interview_started');
    },
    onError: (err: MutationError) => {
      setUiState('start');
      toast.error(err.message || 'Failed to start the interview');
    },
  });

  const submitMutation = trpc.mockInterview.submitAnswer.useMutation({
    onSuccess: (data: {
      questionId: string;
      answerScore: number;
      feedback: string;
      expectedAnswer: string | null;
      completed: boolean;
    }) => {
      setPendingFeedback({
        questionId: data.questionId,
        answerScore: data.answerScore,
        feedback: data.feedback,
        expectedAnswer: data.expectedAnswer,
      });
      if (interviewId) {
        utils.mockInterview.getInterview.invalidate({ interviewId });
      }
      if (data.completed) {
        toast.success('Interview complete!');
        analytics.track('mock_interview_completed');
      }
    },
    onError: (err: MutationError) => {
      toast.error(err.message || 'Failed to evaluate your answer');
    },
  });

  const endMutation = trpc.mockInterview.endInterview.useMutation({
    onSuccess: () => {
      if (interviewId) {
        utils.mockInterview.getInterview.invalidate({ interviewId });
      }
      setUiState('results');
    },
    onError: (err: MutationError) => {
      toast.error(err.message || 'Failed to end the interview');
    },
  });

  const flagMutation = trpc.mockInterview.flagQuestion.useMutation({
    onSuccess: () => {
      toast.success('Thanks — we\'ll review this question.');
    },
    onError: (err: MutationError) => {
      toast.error(err.message || 'Failed to flag the question');
    },
  });

  const handleStart = () => {
    if (!applicationQuery.data) return;
    setUiState('loading');
    startMutation.mutate({ applicationId: applicationQuery.data.id });
  };

  const handleAdvance = () => {
    if (!interviewQuery.data) return;
    const isLast = currentIndex + 1 >= interviewQuery.data.questions.length;
    if (isLast) {
      setUiState('results');
    } else {
      setCurrentIndex((i) => i + 1);
      setPendingFeedback(null);
    }
  };

  const handleEndInterview = (reason: 'abandoned') => {
    if (!interviewId) return;
    endMutation.mutate({ interviewId, reason });
  };

  const handleStartNew = () => {
    setInterviewId(null);
    setCurrentIndex(0);
    setPendingFeedback(null);
    setUiState('start');
    // Clear the ?interviewId= search param so a refresh doesn't bounce back into "view past results" mode.
    // Preserve `from` so the origin-aware "back to application" destination survives a new interview.
    navigate({
      to: '/applications/$lookupId/interview',
      params: { lookupId },
      search: { interviewId: undefined, from },
      replace: true,
    });
  };

  const activeQuestions = useMemo(() => {
    if (!interviewQuery.data) return [];
    return interviewQuery.data.questions.map((q: InterviewQuestion) => ({
      id: q.id,
      orderIndex: q.orderIndex,
      questionText: q.questionText,
      questionType: q.questionType,
    }));
  }, [interviewQuery.data]);

  if (applicationQuery.isLoading) {
    return (
      <div className="mx-auto flex max-w-3xl items-center justify-center p-12">
        <Loader className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    );
  }

  if (applicationQuery.isError || !applicationQuery.data) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
        <Card className="space-y-3 p-8 text-center">
          <p className="font-semibold text-slate-900">Application not found</p>
          <p className="text-sm text-slate-600">
            We couldn't load this application. It may have been deleted, or you
            may not have access to it.
          </p>
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/dashboard/applications' })}
            >
              Back to applications
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const application = applicationQuery.data;
  const usage = usageQuery.data;

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            navigate({
              to: '/applications/$lookupId',
              params: { lookupId },
              search: { from },
            })
          }
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to application
        </Button>
      </div>

      {uiState === 'start' && (
        <InterviewStartScreen
          position={application.position}
          company={application.company}
          hasDescription={!!application.description}
          voiceAvailable={hasSpeechRecognitionSupport()}
          isStarting={false}
          remainingUses={usage ? (usage.isUnlimited ? null : usage.remaining) : null}
          isUnlimited={usage ? usage.isUnlimited : false}
          onStart={handleStart}
        />
      )}

      {uiState === 'loading' && (
        <Card className="space-y-3 p-12 text-center">
          <Loader className="mx-auto h-8 w-8 animate-spin text-slate-500" />
          <p className="text-slate-700">Setting up your interview…</p>
          <p className="text-sm text-slate-500">This usually takes 5–10 seconds.</p>
        </Card>
      )}

      {uiState === 'active' && interviewQuery.data && (
        <InterviewActiveScreen
          interviewId={interviewQuery.data.interviewId}
          avatarUrl={interviewQuery.data.interviewerAvatarUrl}
          voice={interviewQuery.data.interviewerVoice}
          questions={activeQuestions}
          isSubmitting={submitMutation.isLoading}
          isFlagging={flagMutation.isLoading}
          pendingFeedback={pendingFeedback}
          onSubmitAnswer={(params) => {
            if (!interviewId) return;
            submitMutation.mutate({
              interviewId,
              questionId: params.questionId,
              answerText: params.answerText,
              inputMode: params.inputMode,
            });
          }}
          onAdvance={handleAdvance}
          onFlagQuestion={(params) => flagMutation.mutateAsync(params)}
          onEndInterview={handleEndInterview}
          currentIndex={currentIndex}
        />
      )}

      {uiState === 'results' &&
        interviewId &&
        !interviewQuery.data &&
        interviewQuery.isLoading && (
          <Card className="space-y-3 p-12 text-center">
            <Loader className="mx-auto h-8 w-8 animate-spin text-slate-500" />
            <p className="text-slate-700">Loading interview results…</p>
          </Card>
        )}

      {uiState === 'results' &&
        interviewId &&
        !interviewQuery.data &&
        !interviewQuery.isLoading && (
          <Card className="space-y-3 p-8 text-center">
            <p className="font-semibold text-slate-900">
              Interview not available
            </p>
            <p className="text-sm text-slate-600">
              We couldn't load this interview. It may have been deleted, or the
              link may be invalid.
            </p>
            <div className="flex justify-center">
              <Button variant="outline" onClick={handleStartNew}>
                Start a new interview
              </Button>
            </div>
          </Card>
        )}

      {uiState === 'results' && interviewQuery.data && (
        <InterviewResultsScreen
          status={interviewQuery.data.status}
          overallScore={interviewQuery.data.overallScore}
          summaryFeedback={interviewQuery.data.summaryFeedback}
          questions={interviewQuery.data.questions.map((q: InterviewQuestion) => ({
            id: q.id,
            orderIndex: q.orderIndex,
            questionText: q.questionText,
            questionType: q.questionType,
            expectedAnswer:
              'expectedAnswer' in q
                ? (q as { expectedAnswer: string | null }).expectedAnswer
                : null,
            userAnswer: q.userAnswer,
            answerScore: q.answerScore,
            feedback: q.feedback,
          }))}
          onStartNew={handleStartNew}
        />
      )}
    </div>
  );
}
